/**
 * Supervisor workforce hooks (ADH-007 — Patient-Advocate Team, wireframe 4).
 *
 * Wraps the workforce / resourcing surface that the sibling backend worker is
 * adding to a `workforce` service:
 *   - GET  /api/v1/workforce/team             → { items: TeamMember[] } (team table + KPIs)
 *   - GET  /api/v1/workforce/escalations      → { items: Escalation[] } (structured reasons)
 *   - GET  /api/v1/workforce/assignment-rules → { items: AssignmentRule[] } (resourcing config)
 *   - POST /api/v1/workforce/assign           → AssignResult (reassign cases)
 *
 * Each fetch tolerates a bare array and an `{ items }` / `{ cases }` envelope and
 * degrades gracefully: on a 404 (endpoint not yet wired) it falls back to a
 * deterministic seed so the supervisor view — the hero of this phase — always
 * renders the full design. `available` flags record which surfaces were live.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** A member of the patient-advocate team (one row of the team table). */
export interface TeamMember {
  user_id: string;
  name: string;
  role: string;
  /** Active (open) cases assigned to this advocate. */
  active_cases: number;
  /** Cases due today / within the SLA window. */
  due_today: number;
  /** Count of cases at SLA risk (approaching / breached). */
  sla_risk: number;
  /** Conversion rate 0..1 (enrollments / worked). */
  conversion_rate: number;
  /** Capacity utilisation 0..1 (active / target load). */
  utilization?: number | null;
  /** ISO timestamp of last activity. */
  last_active?: string | null;
  status?: string | null;
}

/** A structured escalation needing supervisor attention. */
export interface Escalation {
  id: string;
  case_id?: string | null;
  patient?: string | null;
  /** Structured reason key, e.g. `sla_breach`, `overloaded`, `stuck_stage`. */
  reason: string;
  detail?: string | null;
  severity: string;
  /** Advocate the case currently sits with. */
  owner?: string | null;
  recommended_action?: string | null;
  detected_at?: string | null;
}

/** A resourcing / auto-assignment rule (ADH-007). */
export interface AssignmentRule {
  id: string;
  name: string;
  /** What the rule routes on, e.g. `state`, `language`, `complexity`, `load`. */
  dimension: string;
  /** Human description of the match → assignment behaviour. */
  description: string;
  target?: string | null;
  enabled: boolean;
  priority?: number | null;
}

/** Roll-up KPIs for the totals strip (derived if the API omits them). */
export interface TeamTotals {
  members: number;
  active_cases: number;
  due_today: number;
  sla_risk: number;
  avg_conversion: number;
}

/** AI load-balancing advisory (reassign N cases from X to Y). */
export interface LoadBalanceSuggestion {
  from_user_id: string;
  from_name: string;
  to_user_id: string;
  to_name: string;
  count: number;
  rationale: string;
  confidence: number;
}

/** Body for a reassignment action. */
export interface AssignRequest {
  from_user_id: string;
  to_user_id: string;
  count: number;
  case_ids?: string[];
}

export interface AssignResult {
  reassigned: number;
  from_user_id: string;
  to_user_id: string;
}

function unwrap<T>(data: T[] | { items?: T[]; cases?: T[] } | null | undefined): T[] {
  if (Array.isArray(data)) return data;
  return data?.items ?? data?.cases ?? [];
}

// --- Deterministic demo seed (used only when an endpoint 404s) --------------

const SEED_TEAM: TeamMember[] = [
  { user_id: 'u-amara', name: 'Amara Okafor', role: 'pas', active_cases: 31, due_today: 6, sla_risk: 4, conversion_rate: 0.71, utilization: 1.03, last_active: new Date(Date.now() - 9 * 60_000).toISOString(), status: 'online' },
  { user_id: 'u-diego', name: 'Diego Ramirez', role: 'pas', active_cases: 18, due_today: 3, sla_risk: 1, conversion_rate: 0.64, utilization: 0.6, last_active: new Date(Date.now() - 42 * 60_000).toISOString(), status: 'online' },
  { user_id: 'u-priya', name: 'Priya Nair', role: 'bas', active_cases: 27, due_today: 5, sla_risk: 3, conversion_rate: 0.58, utilization: 0.9, last_active: new Date(Date.now() - 3 * 60_000).toISOString(), status: 'online' },
  { user_id: 'u-jamal', name: 'Jamal Carter', role: 'bas', active_cases: 14, due_today: 1, sla_risk: 0, conversion_rate: 0.62, utilization: 0.47, last_active: new Date(Date.now() - 2 * 3_600_000).toISOString(), status: 'away' },
  { user_id: 'u-sofia', name: 'Sofia Petrov', role: 'retention', active_cases: 22, due_today: 4, sla_risk: 2, conversion_rate: 0.69, utilization: 0.73, last_active: new Date(Date.now() - 18 * 60_000).toISOString(), status: 'online' },
];

const SEED_ESCALATIONS: Escalation[] = [
  { id: 'esc-1', case_id: 'PFA-1042', patient: 'R. Alvarez', reason: 'sla_breach', detail: 'State packet submission overdue by 2 days.', severity: 'high', owner: 'Amara Okafor', recommended_action: 'Reassign to advocate with state-ops capacity', detected_at: new Date(Date.now() - 26 * 3_600_000).toISOString() },
  { id: 'esc-2', case_id: 'PFA-1190', patient: 'T. Nguyen', reason: 'overloaded', detail: 'Owner is at 103% of target load; case idle 3 days.', severity: 'med', owner: 'Amara Okafor', recommended_action: 'Rebalance to Diego Ramirez', detected_at: new Date(Date.now() - 71 * 3_600_000).toISOString() },
  { id: 'esc-3', case_id: 'PFA-1255', patient: 'M. Johnson', reason: 'stuck_stage', detail: 'Stalled in clinical review for 5 days, no attempts logged.', severity: 'med', owner: 'Priya Nair', recommended_action: 'Supervisor outreach + clinical follow-up', detected_at: new Date(Date.now() - 5 * 24 * 3_600_000).toISOString() },
];

const SEED_RULES: AssignmentRule[] = [
  { id: 'ar-1', name: 'State routing → TX desk', dimension: 'state', description: 'Texas Medicaid applications route to the TX state-ops pool.', target: 'pool:tx-state-ops', enabled: true, priority: 1 },
  { id: 'ar-2', name: 'Spanish-preferred → bilingual advocates', dimension: 'language', description: 'Cases with patient language = es assign to bilingual advocates first.', target: 'skill:es', enabled: true, priority: 2 },
  { id: 'ar-3', name: 'High-complexity → senior PAS', dimension: 'complexity', description: 'Clinically-complex cases route to senior advocates only.', target: 'skill:senior', enabled: true, priority: 3 },
  { id: 'ar-4', name: 'Load balancing', dimension: 'load', description: 'Round-robin within an eligible pool, capped at target load.', target: 'pool:eligible', enabled: true, priority: 4 },
];

function deriveTotals(members: TeamMember[]): TeamTotals {
  const sum = members.reduce(
    (acc, m) => {
      acc.active += m.active_cases;
      acc.due += m.due_today;
      acc.risk += m.sla_risk;
      acc.conv += m.conversion_rate;
      return acc;
    },
    { active: 0, due: 0, risk: 0, conv: 0 }
  );
  return {
    members: members.length,
    active_cases: sum.active,
    due_today: sum.due,
    sla_risk: sum.risk,
    avg_conversion: members.length ? sum.conv / members.length : 0,
  };
}

/**
 * Derive an AI load-balancing suggestion from the team: move work from the most
 * over-loaded advocate to the one with the most spare capacity. Surfaced as an
 * advisory only (FND-010) — the supervisor confirms via Reassign.
 */
function deriveSuggestion(members: TeamMember[]): LoadBalanceSuggestion | null {
  if (members.length < 2) return null;
  const withUtil = members.map((m) => ({
    m,
    util: m.utilization ?? m.active_cases / 25,
  }));
  const most = withUtil.reduce((a, b) => (b.util > a.util ? b : a));
  const least = withUtil.reduce((a, b) => (b.util < a.util ? b : a));
  if (most.m.user_id === least.m.user_id || most.util <= 0.85) return null;
  const count = Math.max(1, Math.round((most.m.active_cases - least.m.active_cases) / 4));
  return {
    from_user_id: most.m.user_id,
    from_name: most.m.name,
    to_user_id: least.m.user_id,
    to_name: least.m.name,
    count,
    rationale: `${most.m.name} is at ${Math.round(most.util * 100)}% load with ${most.m.sla_risk} cases at SLA risk; ${least.m.name} has spare capacity (${Math.round(least.util * 100)}%).`,
    confidence: 0.82,
  };
}

interface UseWorkforceResult {
  team: TeamMember[];
  escalations: Escalation[];
  rules: AssignmentRule[];
  totals: TeamTotals;
  suggestion: LoadBalanceSuggestion | null;
  teamAvailable: boolean;
  escalationsAvailable: boolean;
  rulesAvailable: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  /** Reassign `count` cases from one advocate to another (POST /workforce/assign). */
  reassign: (req: AssignRequest) => Promise<boolean>;
}

export function useWorkforce(): UseWorkforceResult {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const [teamAvailable, setTeamAvailable] = useState(true);
  const [escalationsAvailable, setEscalationsAvailable] = useState(true);
  const [rulesAvailable, setRulesAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const load = <T>(
      path: string,
      setRows: (rows: T[]) => void,
      setAvailable: (a: boolean) => void,
      seed: T[]
    ) =>
      apiClient
        .get<T[] | { items?: T[]; cases?: T[] }>(path)
        .then((data) => {
          const rows = unwrap(data);
          setRows(rows.length ? rows : seed);
          setAvailable(true);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            setRows(seed);
            setAvailable(false);
            return;
          }
          throw err;
        });

    const ps = [
      load<TeamMember>('/workforce/team', setTeam, setTeamAvailable, SEED_TEAM),
      load<Escalation>('/workforce/escalations', setEscalations, setEscalationsAvailable, SEED_ESCALATIONS),
      load<AssignmentRule>('/workforce/assignment-rules', setRules, setRulesAvailable, SEED_RULES),
    ];

    Promise.allSettled(ps)
      .then((results) => {
        const failure = results.find((r) => r.status === 'rejected') as
          | PromiseRejectedResult
          | undefined;
        if (failure) {
          const reason = failure.reason;
          setError(reason instanceof ApiError ? reason : new ApiError(0, 'ERROR', String(reason)));
        } else {
          setError(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const reassign = useCallback(async (req: AssignRequest): Promise<boolean> => {
    setIsWorking(true);
    setActionError(null);
    // Optimistic local move so the demo reflects the action even on 404.
    const applyLocal = () =>
      setTeam((rows) =>
        rows.map((m) => {
          if (m.user_id === req.from_user_id) {
            return { ...m, active_cases: Math.max(0, m.active_cases - req.count) };
          }
          if (m.user_id === req.to_user_id) {
            return { ...m, active_cases: m.active_cases + req.count };
          }
          return m;
        })
      );
    try {
      await apiClient.post<AssignResult>('/workforce/assign', req);
      applyLocal();
      return true;
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 404) {
        // Endpoint not wired yet — still reflect the action locally for the demo.
        applyLocal();
        return true;
      }
      setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return false;
    } finally {
      setIsWorking(false);
    }
  }, []);

  const totals = deriveTotals(team);
  const suggestion = deriveSuggestion(team);

  return {
    team,
    escalations,
    rules,
    totals,
    suggestion,
    teamAvailable,
    escalationsAvailable,
    rulesAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    reassign,
  };
}
